import React from "react";
import {
  Container,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  useTheme,
  alpha,
  Chip,
  Stack,
  Button
} from "@mui/material";
import {
  CheckCircle as CheckIcon,
  PhotoCamera as CameraIcon,
  CloudUpload as UploadIcon,
  Security as SecurityIcon,
  Share as ShareIcon,
  Collections as GalleryIcon
} from "@mui/icons-material";

const BeforeAfterPhotos = () => {
  const theme = useTheme();

  const features = [
    {
      icon: <CameraIcon sx={{ fontSize: 40 }} />,
      title: "Time-Stamped Photos",
      description: "Automatic timestamp and location data for every image"
    },
    {
      icon: <UploadIcon sx={{ fontSize: 40 }} />,
      title: "Instant Upload",
      description: "Photos automatically sync to the cloud as you take them"
    },
    {
      icon: <SecurityIcon sx={{ fontSize: 40 }} />,
      title: "Secure Storage",
      description: "Bank-level encryption keeps your photos safe and private"
    },
    {
      icon: <ShareIcon sx={{ fontSize: 40 }} />,
      title: "Easy Sharing",
      description: "One-click sharing with property owners and team members"
    }
  ];

  const benefits = [
    "Visual proof of work completed for every cleaning",
    "Reduce disputes with clear before-and-after evidence",
    "Build trust and transparency with property owners",
    "Identify areas that need extra attention",
    "Create professional reports for clients"
  ];

  const photoComparison = [
    {
      before: "Cluttered living room with used towels",
      after: "Spotless space with fresh linens",
      improvement: "Complete transformation"
    },
    {
      before: "Dirty kitchen with dishes and stains",
      after: "Sanitized kitchen ready for cooking",
      improvement: "Deep sanitization"
    },
    {
      before: "Unmade bed with wrinkled sheets",
      after: "Hotel-quality bed with perfect corners",
      improvement: "Professional bedding"
    },
    {
      before: "Messy bathroom with toiletries",
      after: "Sparkling clean bathroom with amenities",
      improvement: "Thorough sanitization"
    }
  ];

  return (
    <Container maxWidth="lg" sx={{ py: 8 }}>
      {/* Hero Section */}
      <Box sx={{ textAlign: "center", mb: 8 }}>
        <Chip 
          label="📸 Before & After Photos" 
          sx={{ 
            mb: 3,
            background: alpha(theme.palette.primary.main, 0.1),
            color: theme.palette.primary.dark,
            fontWeight: 600,
            fontSize: "0.85rem"
          }}
        />
        <Typography
          variant="h2"
          sx={{
            fontWeight: 700,
            color: theme.palette.text.primary,
            mb: 3,
            fontSize: { xs: "2.5rem", md: "3rem" }
          }}
        >
          Before & After Photos
        </Typography>
        <Typography
          variant="h6"
          sx={{
            color: theme.palette.text.secondary,
            maxWidth: "800px",
            mx: "auto",
            lineHeight: 1.6,
            fontSize: "1.1rem"
          }}
        >
          Capture visual proof of your cleaning excellence with our integrated photo system. 
          Time-stamped before-and-after photos build trust and demonstrate your commitment to quality.
        </Typography>
      </Box>

      {/* Features Grid */}
      <Grid container spacing={4} sx={{ mb: 8 }}>
        {features.map((feature, index) => (
          <Grid item xs={12} sm={6} md={3} key={index}>
            <Card sx={{ textAlign: "center", height: "100%", background: theme.palette.background.paper }}>
              <CardContent sx={{ p: 4 }}>
                <Box
                  sx={{
                    color: theme.palette.primary.main,
                    mb: 2
                  }}
                >
                  {feature.icon}
                </Box>
                <Typography variant="h6" gutterBottom fontWeight="600">
                  {feature.title}
                </Typography>
                <Typography variant="body2" color={theme.palette.text.secondary}>
                  {feature.description}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Benefits & Process */}
      <Grid container spacing={6} sx={{ mb: 8 }}>
        <Grid item xs={12} md={6}>
          <Typography variant="h3" gutterBottom sx={{ fontWeight: 700, mb: 3 }}>
            Why Photo Documentation Matters
          </Typography>
          
          <Stack spacing={2} sx={{ mb: 4 }}>
            {benefits.map((benefit, index) => (
              <Box key={index} sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                <CheckIcon sx={{ color: theme.palette.primary.main, mt: 0.5 }} />
                <Typography variant="body1" fontWeight="500">
                  {benefit}
                </Typography>
              </Box>
            ))}
          </Stack>

          <Button 
            variant="contained" 
            size="large"
            startIcon={<GalleryIcon />}
            sx={{
              background: theme.palette.primary.main,
              borderRadius: "8px",
              px: 4,
              py: 1.5,
              fontWeight: 600
            }}
          >
            View Sample Gallery
          </Button>
        </Grid>

        <Grid item xs={12} md={6}>
          <Box
            sx={{
              background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)} 0%, ${alpha(theme.palette.secondary.main, 0.05)} 100%)`,
              borderRadius: 3,
              p: 4,
              height: '100%'
            }}
          >
            <Typography variant="h5" gutterBottom fontWeight="600" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CameraIcon />
              How It Works
            </Typography>
            
            <Stack spacing={3}>
              {[
                { step: "1", text: "Take before photos when you arrive at the property" },
                { step: "2", text: "Complete your cleaning checklist as usual" },
                { step: "3", text: "Capture after photos of each cleaned area" },
                { step: "4", text: "Photos automatically sync and organize in the app" },
                { step: "5", text: "Share with property owners or use for quality review" }
              ].map((item, index) => (
                <Box key={index} sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                  <Box
                    sx={{
                      width: 32,
                      height: 32,
                      borderRadius: '50%',
                      background: theme.palette.primary.main,
                      color: 'white',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: 'bold',
                      fontSize: '0.875rem',
                      flexShrink: 0
                    }}
                  >
                    {item.step}
                  </Box>
                  <Typography variant="body1" color={theme.palette.text.primary}>
                    {item.text}
                  </Typography>
                </Box>
              ))}
            </Stack>
          </Box>
        </Grid>
      </Grid>

      {/* Photo Comparison Examples */}
      <Box sx={{ mb: 8 }}>
        <Typography variant="h3" textAlign="center" gutterBottom sx={{ fontWeight: 700, mb: 4 }}>
          See the Transformation
        </Typography>
        <Grid container spacing={3}>
          {photoComparison.map((comparison, index) => (
            <Grid item xs={12} sm={6} key={index}>
              <Card sx={{ background: theme.palette.background.paper, overflow: 'hidden' }}>
                <CardContent sx={{ p: 0 }}>
                  <Box sx={{ display: 'flex', height: 120 }}>
                    <Box 
                      sx={{ 
                        flex: 1, 
                        background: alpha(theme.palette.error.main, 0.1),
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexDirection: 'column',
                        p: 2
                      }}
                    >
                      <Typography variant="body2" fontWeight="600" color={theme.palette.error.main}>
                        BEFORE
                      </Typography>
                      <Typography variant="body2" textAlign="center" color={theme.palette.text.secondary}>
                        {comparison.before}
                      </Typography>
                    </Box>
                    <Box 
                      sx={{ 
                        flex: 1, 
                        background: alpha(theme.palette.success.main, 0.1),
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexDirection: 'column',
                        p: 2
                      }}
                    >
                      <Typography variant="body2" fontWeight="600" color={theme.palette.success.main}>
                        AFTER
                      </Typography>
                      <Typography variant="body2" textAlign="center" color={theme.palette.text.secondary}>
                        {comparison.after}
                      </Typography>
                    </Box>
                  </Box>
                  <Box sx={{ p: 2, borderTop: `1px solid ${alpha(theme.palette.primary.main, 0.1)}` }}>
                    <Typography variant="body2" fontWeight="600" color={theme.palette.primary.main}>
                      {comparison.improvement}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>
    </Container>
  );
};

export default BeforeAfterPhotos;